import {
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { isMobile } from 'react-device-detect'
import styled from 'styled-components'

import { plusComma } from '../../js/script'
import { postSupportCoin } from '../../api/login'
import { getAccumulations } from '../../api/profile'
import { getProfile } from '../../api/auth'

import { SectionTitle } from '../title/SectionTitle'
import { InputText } from '../input/InputText'
import { BtnBlack } from '../Button'
import { ToggleRadioButton } from '../input/InputRadioButton'
import PointExchangeCenterModal from '../myshopping/PointExchangeCenterModal'
import PointExchange from '../myshopping/PointExchangeOrder'

import { useCommonContext } from '../provider/CommonProvider'
import { useMyContext } from '../provider/MyProvider'

import { OrderProductWithUsable } from './NewOrder'
import { ProfileRootType } from '../../@types/auth'
import { GetOrderSheetsOrderSheetNoResponse, PaymentInfo } from '@shopby/shop-sdk'

export interface OrderAccumulationSectionHandle {
  setInputValue: (value: number) => void
  focus: () => void
  resetInput: () => void
}

interface OrderAccumulationProps {
  orderItems: OrderProductWithUsable[]
  couponAdaptedResult?: GetOrderSheetsOrderSheetNoResponse
  paymentInfo: PaymentInfo
  isPointChanged: boolean
  setIsPointChanged: Dispatch<SetStateAction<boolean>>
  setAccumulationUseAmt: Dispatch<SetStateAction<number>>
  onReady?: (ready: boolean) => void // 추가: 컴포넌트 준비 상태를 알리는 콜백
}

const OrderAccumulationSection = forwardRef<OrderAccumulationSectionHandle, OrderAccumulationProps>(
  ({ orderItems, paymentInfo, isPointChanged, setIsPointChanged, setAccumulationUseAmt, onReady }, ref) => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isFirstApply, setIsFirstApply] = useState(true)

    const [coin, setCoin] = useState(0)
    const [inputAcc, setInputAcc] = useState(0) // 입력된 적립금
    const [accumulationAmount, setAccumulateAmount] = useState(0)

    const [isCheckboxAllPoint, setIsCheckboxAllPoint] = useState(false)
    const [checkBtn, setCheckBtn] = useState(false)
    const [pointModal, setPointModal] = useState(false)

    const [userData, setUserData] = useState<ProfileRootType>() // 사용자 데이터
    const [accumulationUnusableItems, setAccumulationUnusableItems] = useState<OrderProductWithUsable[]>([]) // 적립금 사용 불가 상품

    const [allProductAmount, setAllProductAmount] = useState(0) // 전체 상품 금액
    const [minPrice, setMinPrice] = useState(10000) // 최소 결제 금액
    const [accumulationUseMaxRate, setAccumulationUseMaxRate] = useState(0) // 적립금 사용 최대 비율
    const [availableMaxAccumulationAmt, setAvailableMaxAccumulationAmt] = useState(0) // 사용 가능한 최대 적립금

    const { hasAccessToken } = useCommonContext()
    const { accumulationConfig, setToastMessage, setIsToast } = useMyContext()

    const inputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => ({
      setInputValue: (value: number) => {
        setInputAcc(value)
      },
      focus: () => {
        inputRef.current?.focus()
      },
      resetInput: () => {
        setInputAcc(0)
        handleToggleAllPoint()
      },
    }))

    // input change 핸들러
    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(
          e.target.value
            .replace(/,/g, '')
            .replace(/-/g, '')
            .replace(/[^0-9]/g, '')
        )
        setCheckBtn(false)
        setIsCheckboxAllPoint(false)

        const maxAvailable = paymentInfo?.availableMaxAccumulationAmt ?? 0
        const minPriceLimit = paymentInfo?.minPriceLimit || 10000
        const paymentAmt = paymentInfo?.paymentAmt ?? 0

        // 상품가 10,000원 미만이면 입력값을 0으로 제한
        if (paymentAmt < minPriceLimit) {
          if (!isFirstApply) alert(`적립금은 ${plusComma(minPriceLimit)}원 이상부터 사용 가능합니다.`)
          setInputAcc(0)
          return
        }

        if (value > maxAvailable) {
          alert('사용 가능한 적립금 한도를 초과했습니다.')
          setInputAcc(Math.floor(maxAvailable / 1000) * 1000)
          // setInputAcc(0)
          return
        }

        setInputAcc(value)
      },
      [paymentInfo]
    )

    // 적립금 적용 버튼 핸들러
    const handleApply = useCallback(() => {
      const acc = Number(inputAcc)
      if (paymentInfo && paymentInfo.paymentAmt < minPrice) {
        if (!isFirstApply) alert(`${plusComma(minPrice)}원 이상부터 사용 가능합니다.`)
        setInputAcc(0)
        setAccumulationUseAmt(0)
        setCheckBtn(false)
        setIsCheckboxAllPoint(false)
        return
      }
      if (acc % 1000 !== 0 && acc !== 0) {
        alert('1000원 단위로만 사용 가능합니다.')
        setInputAcc(0)
        setAccumulationUseAmt(0)
        setCheckBtn(false)
        setIsCheckboxAllPoint(false)
        return
      }
      setAccumulationUseAmt(acc) // 0이든 아니든 무조건 전달
      if (acc !== 0) {
        setCheckBtn(true)
        setIsToast(true)
        setToastMessage('적립금이 적용되었습니다.')
        const maxAvailable = paymentInfo?.availableMaxAccumulationAmt ?? 0
        const maxByUnit = Math.floor(maxAvailable / 1000) * 1000
        setIsCheckboxAllPoint(acc === maxByUnit)
      } else {
        setCheckBtn(false)
        setIsCheckboxAllPoint(false)
      }
    }, [inputAcc, minPrice, paymentInfo])

    // 모두 적용 불가 클릭
    const handleAllApplyDisabled = useCallback(() => {
      if (accumulationUnusableItems?.length === orderItems?.length) {
        alert('적립금을 사용할 수 없는 상품입니다.')
      }
    }, [accumulationUnusableItems, orderItems])

    const handleToggleAllPoint = useCallback(() => {
      const minPriceLimit = paymentInfo?.minPriceLimit || 10000
      const paymentAmt = paymentInfo?.paymentAmt ?? 0

      // 상품가 10,000원 미만이면 모두 적용 불가
      if (paymentAmt < minPriceLimit) {
        if (!isFirstApply) alert(`${plusComma(minPrice)}원 이상부터 사용 가능합니다.`)
        setInputAcc(0)
        setAccumulationUseAmt(0)
        setCheckBtn(false)
        setIsCheckboxAllPoint(false)
        return
      }

      const isAvailable = accumulationAmount > 0 && paymentInfo && paymentInfo.availableMaxAccumulationAmt > 0
      if (!isAvailable) {
        handleAllApplyDisabled()
        return
      }
      if (!isCheckboxAllPoint) {
        const maxAvailable = paymentInfo?.availableMaxAccumulationAmt ?? 0
        const maxByUnit = Math.floor(maxAvailable / 1000) * 1000
        setInputAcc(maxByUnit)
        setAccumulationUseAmt(maxByUnit)
        setCheckBtn(true)
        setIsCheckboxAllPoint(true)
        setToastMessage('사용가능한 최대 적립금이 적용되었습니다.')
      } else {
        // 해제
        setAccumulationUseAmt(0)
        setInputAcc(0)
        setCheckBtn(false)
        setIsCheckboxAllPoint(false)
      }
    }, [accumulationAmount, paymentInfo, isCheckboxAllPoint, handleAllApplyDisabled])

    // 리셋 버튼
    const handleReset = useCallback(() => {
      setInputAcc(0)
      setIsCheckboxAllPoint(false)
      setCheckBtn(false)
    }, [])

    useEffect(() => {
      if (orderItems) {
        const filterItem = orderItems.filter((item) => !item.accumulationUsable)
        setAccumulationUnusableItems(filterItem)
      }
    }, [orderItems])

    useEffect(() => {
      const fetchData = async () => {
        if (!hasAccessToken) return
        try {
          setIsLoading(true)
          // 프로필, 적립금, 코인 정보 병렬 처리
          const [profileRes, accumulationRes, coinRes] = await Promise.all([
            getProfile(),
            getAccumulations('ADD'),
            postSupportCoin('total'),
          ])
          setUserData(profileRes.data)
          setAccumulateAmount(accumulationRes.data.totalAmt)
          setCoin(coinRes.data.totalCoin)
        } catch (err) {
          console.log(err)
        } finally {
          setIsLoading(false)
        }
      }
      fetchData()
    }, [isPointChanged, hasAccessToken])

    useEffect(() => {
      setAccumulationUseMaxRate(accumulationConfig?.accumulationUseMaxRate || 10)
    }, [accumulationConfig])

    useEffect(() => {
      // 적립금 입력 필드 비활성화 판단: (비회원, 상품가 1만원 미만 등)면 true
      const isDisabled =
        !paymentInfo ||
        !paymentInfo.availableMaxAccumulationAmt ||
        (paymentInfo.paymentAmt ?? 0) < minPrice ||
        !hasAccessToken

      // 적립금 입력 필드가 활성화되어 있고 입력값이 유효할 때 true
      const isInputValid = inputAcc % 1000 === 0 && inputAcc >= 0 && inputAcc <= availableMaxAccumulationAmt

      // 준비 완료 조건 체크
      const isReady = isDisabled || isInputValid

      onReady?.(isReady)
    }, [inputAcc, paymentInfo, minPrice, availableMaxAccumulationAmt, hasAccessToken, onReady])

    useEffect(() => {
      const max = paymentInfo.availableMaxAccumulationAmt || 0
      const value = Math.floor(max / 1000) * 1000
      const minPriceLimit = paymentInfo.minPriceLimit || 10000
      const totalStandardAmt = paymentInfo.totalStandardAmt || 0
      const totalImmediateDiscountAmt =
        (paymentInfo?.totalImmediateDiscountAmt || 0) + (paymentInfo?.totalAdditionalDiscountAmt || 0)
      const productCouponAmt = paymentInfo?.productCouponAmt || 0
      const cartCouponAmt = paymentInfo?.cartCouponAmt || 0
      const totalProductAmt = totalStandardAmt - totalImmediateDiscountAmt - productCouponAmt - cartCouponAmt
      setMinPrice(minPriceLimit)
      setAllProductAmount(totalProductAmt)

      // 상품가 10,000원 미만이면 사용 가능 적립금 0으로 표시
      if ((paymentInfo.paymentAmt ?? 0) < minPriceLimit) {
        setAvailableMaxAccumulationAmt(0)
      } else {
        setAvailableMaxAccumulationAmt(value)
      }

      if (isFirstApply && value > 0 && (paymentInfo.paymentAmt ?? 0) >= (paymentInfo.minPriceLimit ?? 0)) {
        setTimeout(() => {
          handleToggleAllPoint()
        }, 0)
        setIsFirstApply(false) // 최초 1회만 실행
      }
    }, [paymentInfo, paymentInfo.availableMaxAccumulationAmt])

    return (
      <Section>
        <div className='point_container'>
          <SectionTitle>적립금 전환</SectionTitle>
          <div className='point_transform_wrap'>
            {/* 피싱코인 */}
            <div className='fishing_coin'>
              <div className='coin_num'>
                {isLoading ? (
                  <SpinnerContainer>
                    <LoadingSpinner />
                  </SpinnerContainer>
                ) : (
                  <p>{plusComma(coin)}</p>
                )}

                <img src='/images/icon_coin.png' alt='coin' />
              </div>
              <div className='coin_text'>
                <p>피싱코인</p>
              </div>
            </div>
            <button className='btn_transform' onClick={() => setPointModal(true)}>
              전환
            </button>
            {/* 적립금 */}
            <div className='point'>
              <div className='point_num'>
                {isLoading ? (
                  <SpinnerContainer>
                    <LoadingSpinner />
                  </SpinnerContainer>
                ) : (
                  <p>{plusComma(accumulationAmount)}</p>
                )}
                <img src='/images/icon_point.png' alt='coin' />
              </div>
              <div className='point_text'>
                <p>보유 적립금</p>
              </div>
            </div>
          </div>
          {/* 내 보유 적립금, 사용 가능 적립금 */}
          <StyledMyPointGroup>
            <MyPointRow>
              <span className='label'>사용 가능 적립금</span>
              <span className='value'>
                {plusComma(availableMaxAccumulationAmt)}원
                <img src='/images/icon_point.png' alt='coin' />
              </span>
            </MyPointRow>
            {checkBtn && paymentInfo && inputAcc > 0 && inputAcc % 1000 === 0 && paymentInfo.paymentAmt >= minPrice && (
              <>
                <Divider />
                <MyPointRow>
                  <span className='label'>상품 총합가</span>
                  <span className='value'>
                    {plusComma(allProductAmount)}
                    <img src='/images/icon_point.png' alt='coin' />
                  </span>
                </MyPointRow>
                <MyPointRow>
                  <span className='label'>사용 적립금</span>
                  <span className='value minus'>
                    - {plusComma(inputAcc)}
                    <img src='/images/icon_point.png' alt='coin' />
                  </span>
                </MyPointRow>
                <Divider />
                <MyPointRow style={{ marginBottom: '4px' }}>
                  <span className='label strong'>적립금 적용가</span>
                  <span className='value strong'>
                    {plusComma(paymentInfo.paymentAmt - paymentInfo.deliveryAmt - paymentInfo.remoteDeliveryAmt)}
                    <img src='/images/icon_point.png' alt='coin' />
                  </span>
                </MyPointRow>
              </>
            )}
          </StyledMyPointGroup>
          {/* 적립금 변환 Input */}
          <div className='point_exchange_container'>
            <div className='input_wrap'>
              <InputText
                ref={inputRef}
                type='text'
                inputMode='numeric'
                value={inputAcc === 0 ? '' : plusComma(Number(inputAcc))}
                placeholder='0'
                disabled={
                  !paymentInfo || !paymentInfo.availableMaxAccumulationAmt || (paymentInfo.paymentAmt ?? 0) < minPrice
                }
                onChange={handleInputChange}
              />
              {inputAcc !== 0 && <button type='button' className='pointReset' onClick={handleReset}></button>}
            </div>
            <BtnBlack
              className='btn_point_apply'
              onClick={handleApply}
              disabled={
                checkBtn || !paymentInfo || (paymentInfo.paymentAmt ?? 0) < minPrice // 상품가 10,000원 미만이면 비활성화
              }
              style={checkBtn ? { color: 'var(--colGray4)', background: 'var(--colGray2)' } : undefined}
            >
              {checkBtn ? '적용중' : '적용하기'}
            </BtnBlack>
          </div>
          <div className='all_apply'>
            <ToggleRadioButton
              name='allPoint'
              value='Y'
              checked={isCheckboxAllPoint}
              onChange={handleToggleAllPoint}
              label='모두 적용'
              size='small'
              disabled={
                !paymentInfo || !paymentInfo.availableMaxAccumulationAmt || (paymentInfo.paymentAmt ?? 0) < minPrice // 상품가 10,000원 미만이면 비활성화
              }
            />
          </div>
        </div>
        <NoticeList>
          <li>
            적립금은 상품가 {plusComma(minPrice)}원 이상일 때 1,000원 단위로, 주문 금액의 {accumulationUseMaxRate}%까지
            사용 가능합니다.
          </li>
          <li>적립금은 상품 구매 시에만 사용 가능하며, 일부 상품에는 적용이 제한될 수 있습니다.</li>
        </NoticeList>

        {pointModal &&
          (isMobile ? (
            <PointExchange
              coin={coin}
              memberNo={userData?.memberNo}
              setIsModal={setPointModal}
              setPointChange={setIsPointChanged}
            />
          ) : (
            <PointExchangeCenterModal
              coin={coin}
              memberNo={userData?.memberNo}
              setIsModal={setPointModal}
              setChangePointCheck={setIsPointChanged}
            />
          ))}
      </Section>
    )
  }
)

OrderAccumulationSection.displayName = 'OrderAccumulationSection'

const Section = styled.div`
  padding: 24px 20px;
  @media screen and (min-width: 768px) {
    padding: 32px 0 32px 0;
    border-bottom: 1px solid var(--colGray3);
    &:last-child {
      border-bottom: none;
    }
  }
`

const StyledMyPointGroup = styled.div`
  margin-top: 18px;
  background: #f5faff;
  border-radius: 10px;
  padding: 18px 20px 10px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MyPointRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  .label {
    color: #607d8b;
    font-size: 15px;
    font-weight: 500;
  }
  .label.strong {
    color: #0d47a1;
    font-weight: 700;
  }
  .value {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    color: #222;
    gap: 4px;
  }
  .value.minus {
    color: #1976d2;
    font-weight: 700;
  }
  .value.strong {
    color: #0d47a1;
    font-size: 18px;
    font-weight: 700;
  }
  img {
    width: 18px;
    height: 18px;
    margin-left: 4px;
  }
`

const Divider = styled.hr`
  border: none;
  border-top: 1px dashed #b0bec5;
  margin: 8px 0;
  @media screen and (min-width: 768px) {
    display: block;
  }
`

const NoticeList = styled.ul`
  margin-top: 8px;
  padding: 0;
  line-height: 1.5;
  color: var(--colGray5);
  list-style: none;

  li {
    margin-top: 2px;
    font-size: 14px;
    position: relative;
    padding-left: 20px;
    display: flex;
    align-items: center;
  }
  li::before {
    content: '*';
    color: var(--colGray5); /* 원하는 색상으로 변경 가능 */
    font-size: 14px;
    position: absolute;
    left: 0;
    top: 6px;
    line-height: 1;
    width: 20px;
    text-align: center;
    display: inline-block;
  }
`

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

export const LoadingSpinner = styled.div`
  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  display: flex;
  margin: auto;
  width: 22px;
  height: 22px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #898989;
  animation: spinner 1s ease infinite;

  @media screen and (min-width: 768px) {
    width: 32px;
    height: 32px;
  }
`

export default OrderAccumulationSection
